import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  BarChart3, 
  Mail, 
  Send, 
  InboxIcon, 
  TrendingUp, 
  Users, 
  Clock, 
  Calendar,
  ChevronDown,
  Loader2,
  AlertCircle,
  Tag
} from 'lucide-react';
import AppShell from '../components/layout/AppShell';
import { mailApi } from '../api/client';
import api from '../api/client';
import { cn } from '../utils/cn';

interface TopSender {
  email: string;
  name: string;
  count: number;
  percentage: number;
}

interface VolumeData {
  date: string;
  received: number;
  sent: number;
}

interface PeakHour {
  hour: number;
  count: number;
}

interface CategoryStats {
  name: string;
  count: number;
  percentage: number;
  color?: string;
}

interface Analytics {
  overview: {
    totalReceived: number;
    totalSent: number;
    totalUnread: number;
    averagePerDay: number;
    period: {
      start: string;
      end: string;
      days: number;
    };
  };
  topSenders: TopSender[];
  volumeTrend: VolumeData[];
  peakHours: PeakHour[];
  categoryBreakdown: CategoryStats[];
}

const PERIOD_OPTIONS = [
  { value: 7, label: 'Letzte 7 Tage' },
  { value: 14, label: 'Letzte 14 Tage' },
  { value: 30, label: 'Letzte 30 Tage' },
  { value: 90, label: 'Letzte 3 Monate' },
];

export default function AnalyticsPage() {
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  
  // Fetch mail accounts
  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: () => mailApi.getAccounts(),
    staleTime: 5 * 60 * 1000,
  });
  
  const accounts = (accountsData?.accounts || []) as Array<{ id: string; providerEmail: string; provider: string }>;
  
  // Set default account
  const accountId = selectedAccountId || accounts[0]?.id;
  
  // Fetch analytics
  const { data: analyticsData, isLoading: analyticsLoading, error } = useQuery({
    queryKey: ['analytics', accountId, selectedPeriod],
    queryFn: async () => {
      const response = await api.get(`analytics/${accountId}?days=${selectedPeriod}`).json<{ analytics: Analytics }>();
      return response.analytics;
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
  
  const isLoading = accountsLoading || analyticsLoading;
  
  // Calculate max volume for chart scaling
  const maxVolume = useMemo(() => {
    if (!analyticsData?.volumeTrend) return 1;
    return Math.max(
      ...analyticsData.volumeTrend.map(v => Math.max(v.received, v.sent)),
      1
    );
  }, [analyticsData?.volumeTrend]);
  
  // Get peak hour stats
  const peakHourStats = useMemo(() => {
    if (!analyticsData?.peakHours?.length) return null;
    const sorted = [...analyticsData.peakHours].sort((a, b) => b.count - a.count);
    const peak = sorted[0];
    return {
      hour: peak.hour,
      count: peak.count,
      formatted: `${peak.hour.toString().padStart(2, '0')}:00 - ${(peak.hour + 1).toString().padStart(2, '0')}:00`,
    };
  }, [analyticsData?.peakHours]);
  
  if (accounts.length === 0 && !accountsLoading) {
    return (
      <AppShell>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-tertiary)' }} />
            <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Kein E-Mail-Konto verbunden
            </h2>
            <p style={{ color: 'var(--text-muted)' }}>
              Verbinde zuerst ein E-Mail-Konto, um Analytics zu sehen.
            </p>
          </div>
        </div>
      </AppShell>
    );
  }
  
  return (
    <AppShell>
      <div className="flex-1 overflow-auto p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              📈 Email Analytics
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
              Einblicke in deine E-Mail-Aktivität
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Account Selector */}
            {accounts.length > 1 && (
              <select
                value={accountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-default)',
                  color: 'var(--text-primary)',
                }}
              >
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.providerEmail}
                  </option>
                ))}
              </select>
            )}
            
            {/* Period Selector */}
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
              className="px-3 py-2 rounded-lg text-sm"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-default)',
                color: 'var(--text-primary)',
              }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--brand-primary)' }} />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-error)' }} />
              <p style={{ color: 'var(--text-primary)' }}>Analytics konnten nicht geladen werden</p>
            </div>
          </div>
        ) : analyticsData ? (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                icon={<InboxIcon className="w-5 h-5" />}
                label="Empfangen"
                value={analyticsData.overview.totalReceived.toLocaleString('de-DE')}
                subtext={`Ø ${analyticsData.overview.averagePerDay}/Tag`}
                color="var(--brand-primary)"
              />
              <StatCard
                icon={<Send className="w-5 h-5" />}
                label="Gesendet"
                value={analyticsData.overview.totalSent.toLocaleString('de-DE')}
                color="#10b981"
              />
              <StatCard
                icon={<Mail className="w-5 h-5" />}
                label="Ungelesen"
                value={analyticsData.overview.totalUnread.toLocaleString('de-DE')}
                color="#f59e0b"
              />
              <StatCard
                icon={<Clock className="w-5 h-5" />}
                label="Peak-Stunde"
                value={peakHourStats?.formatted || '-'}
                subtext={peakHourStats ? `${peakHourStats.count} Mails` : undefined}
                color="#8b5cf6"
              />
            </div>
            
            {/* Charts Grid */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Volume Trend */}
              <div 
                className="rounded-xl p-5"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-default)',
                }}
              >
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <TrendingUp className="w-5 h-5" style={{ color: 'var(--brand-primary)' }} />
                  Volumen-Trend
                </h3>
                <div className="h-48">
                  <VolumeTrendChart data={analyticsData.volumeTrend} maxValue={maxVolume} />
                </div>
              </div>
              
              {/* Top Senders */}
              <div 
                className="rounded-xl p-5"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-default)',
                }}
              >
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Users className="w-5 h-5" style={{ color: 'var(--brand-primary)' }} />
                  Top Absender
                </h3>
                <div className="space-y-3">
                  {analyticsData.topSenders.slice(0, 5).map((sender, i) => (
                    <div key={sender.email} className="flex items-center gap-3">
                      <span 
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ 
                          backgroundColor: 'var(--brand-primary)',
                          color: 'white',
                        }}
                      >
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                          {sender.name}
                        </p>
                        <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                          {sender.email}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {sender.count}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {sender.percentage}%
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Peak Hours */}
              <div 
                className="rounded-xl p-5"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-default)',
                }}
              >
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Clock className="w-5 h-5" style={{ color: 'var(--brand-primary)' }} />
                  Aktivität nach Uhrzeit
                </h3>
                <div className="h-32">
                  <HourlyChart data={analyticsData.peakHours} />
                </div>
              </div>
              
              {/* Category Breakdown */}
              <div 
                className="rounded-xl p-5"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-default)',
                }}
              >
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Tag className="w-5 h-5" style={{ color: 'var(--brand-primary)' }} />
                  Kategorien
                </h3>
                <div className="space-y-2">
                  {analyticsData.categoryBreakdown.slice(0, 6).map((cat) => (
                    <div key={cat.name} className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: cat.color || 'var(--brand-primary)' }}
                      />
                      <span className="flex-1 text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                        {cat.name}
                      </span>
                      <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                        {cat.count} ({cat.percentage}%)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AppShell>
  );
}

// Stat Card Component
function StatCard({ 
  icon, 
  label, 
  value, 
  subtext, 
  color 
}: { 
  icon: React.ReactNode; 
  label: string; 
  value: string; 
  subtext?: string;
  color: string;
}) {
  return (
    <div 
      className="rounded-xl p-4"
      style={{ 
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-default)',
      }}
    >
      <div className="flex items-center gap-3 mb-2">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}20`, color }}
        >
          {icon}
        </div>
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{label}</span>
      </div>
      <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
        {value}
      </p>
      {subtext && (
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          {subtext}
        </p>
      )}
    </div>
  );
}

// Volume Trend Chart (Simple CSS-based bar chart)
function VolumeTrendChart({ data, maxValue }: { data: VolumeData[]; maxValue: number }) {
  // Show last 14 days max for readability
  const displayData = data.slice(-14);
  
  return (
    <div className="flex items-end gap-1 h-full">
      {displayData.map((d, i) => {
        const receivedHeight = (d.received / maxValue) * 100;
        const sentHeight = (d.sent / maxValue) * 100;
        const date = new Date(d.date);
        const dayLabel = date.toLocaleDateString('de-DE', { weekday: 'short' });
        
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.received} empfangen, ${d.sent} gesendet`}>
            <div className="flex-1 w-full flex items-end gap-0.5">
              <div 
                className="flex-1 rounded-t-sm transition-all duration-300"
                style={{ 
                  height: `${receivedHeight}%`,
                  backgroundColor: 'var(--brand-primary)',
                  minHeight: d.received > 0 ? '4px' : '0',
                }}
              />
              <div 
                className="flex-1 rounded-t-sm transition-all duration-300"
                style={{ 
                  height: `${sentHeight}%`,
                  backgroundColor: '#10b981',
                  minHeight: d.sent > 0 ? '4px' : '0',
                }}
              />
            </div>
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              {i % 2 === 0 ? dayLabel : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Hourly Chart
function HourlyChart({ data }: { data: PeakHour[] }) {
  const sortedByHour = [...data].sort((a, b) => a.hour - b.hour);
  const maxCount = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end gap-px h-full">
      {sortedByHour.map((d) => {
        const height = (d.count / maxCount) * 100;
        const isWorkHour = d.hour >= 9 && d.hour <= 17;
        
        return (
          <div 
            key={d.hour} 
            className="flex-1 flex flex-col items-center"
            title={`${d.hour}:00 - ${d.hour + 1}:00: ${d.count} Mails`}
          >
            <div 
              className="w-full rounded-t-sm transition-all duration-300 cursor-pointer hover:opacity-80"
              style={{ 
                height: `${height}%`,
                backgroundColor: isWorkHour ? 'var(--brand-primary)' : 'var(--text-tertiary)',
                minHeight: d.count > 0 ? '2px' : '0',
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
